import { z } from "zod";

export const datasetFilterSchema = z.object({
  id: z.string(),
  value: z.object({
    op: z.enum(["eq", "ne", "gt", "gte", "lt", "lte", "anyof", "in", "contains"]),
    value: z.union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))]),
  }),
});

export type DatasetsQueryFilter = z.infer<typeof datasetFilterSchema>;

export const dspDatasetsPaginationSchema = z.object({
  page: z.number(),
  limit: z.number().nullable(),
  total: z.number().nullable(),
});

export const parseJson = <T>(schema: z.ZodType<T>, value: string, fallback: T): T => {
  if (typeof value !== "string") return fallback;
  try {
    const parsed = JSON.parse(value);
    const res = schema.safeParse(parsed);
    return res.success ? res.data : fallback;
  } catch {
    return fallback;
  }
};

export const dspFrequencySchema = z.object({
  frequencyType: z.string(),
  levelType: z.string().optional(),
  maxImpressions: z.number().optional(),
  timeUnit: z.string().optional(),
  timeUnitCount: z.number().optional(),
  extraFrequencyCapImpressionSet: z.array(z.unknown()).optional(),
});

export const dspSharedMetricFields = {
  impressions: z.number(),
  impressions_prev: z.number().nullable().optional(),
  impressions_delta: z.number().nullable().optional(),
  impressions_percent: z.number().nullable().optional(),

  clicks: z.number(),
  clicks_prev: z.number().nullable().optional(),
  clicks_delta: z.number().nullable().optional(),
  clicks_percent: z.number().nullable().optional(),

  total_cost: z.number(),
  total_cost_prev: z.number().nullable().optional(),
  total_cost_delta: z.number().nullable().optional(),
  total_cost_percent: z.number().nullable().optional(),

  total_purchases: z.number(),
  total_purchases_prev: z.number().nullable().optional(),
  total_purchases_delta: z.number().nullable().optional(),
  total_purchases_percent: z.number().nullable().optional(),

  total_sales: z.number(),
  total_sales_prev: z.number().nullable().optional(),
  total_sales_delta: z.number().nullable().optional(),
  total_sales_percent: z.number().nullable().optional(),

  total_units_sold: z.number(),
  total_units_sold_prev: z.number().nullable().optional(),
  total_units_sold_delta: z.number().nullable().optional(),
  total_units_sold_percent: z.number().nullable().optional(),

  total_detail_page_views: z.number(),
  total_detail_page_views_prev: z.number().nullable().optional(),
  total_detail_page_views_delta: z.number().nullable().optional(),
  total_detail_page_views_percent: z.number().nullable().optional(),

  total_add_to_cart: z.number(),
  total_add_to_cart_prev: z.number().nullable().optional(),
  total_add_to_cart_delta: z.number().nullable().optional(),
  total_add_to_cart_percent: z.number().nullable().optional(),

  new_to_brand_detail_page_views: z.number(),
  new_to_brand_detail_page_views_prev: z.number().nullable().optional(),
  new_to_brand_detail_page_views_delta: z.number().nullable().optional(),
  new_to_brand_detail_page_views_percent: z.number().nullable().optional(),

  total_new_to_brand_dpvs: z.number(),
  total_new_to_brand_dpvs_prev: z.number().nullable().optional(),
  total_new_to_brand_dpvs_delta: z.number().nullable().optional(),
  total_new_to_brand_dpvs_percent: z.number().nullable().optional(),

  brand_searches: z.number(),
  brand_searches_prev: z.number().nullable().optional(),
  brand_searches_delta: z.number().nullable().optional(),
  brand_searches_percent: z.number().nullable().optional(),

  click_thru_rate: z.number().nullable(),
  click_thru_rate_prev: z.number().nullable().optional(),
  click_thru_rate_delta: z.number().nullable().optional(),
  click_thru_rate_percent: z.number().nullable().optional(),

  tacos: z.number().nullable(),
  tacos_prev: z.number().nullable().optional(),
  tacos_delta: z.number().nullable().optional(),
  tacos_percent: z.number().nullable().optional(),

  troas: z.number().nullable(),
  troas_prev: z.number().nullable().optional(),
  troas_delta: z.number().nullable().optional(),
  troas_percent: z.number().nullable().optional(),

  ecpm: z.number().nullable(),
  ecpm_prev: z.number().nullable().optional(),
  ecpm_delta: z.number().nullable().optional(),
  ecpm_percent: z.number().nullable().optional(),

  total_detail_page_view_rate: z.number().nullable(),
  total_detail_page_view_rate_prev: z.number().nullable().optional(),
  total_detail_page_view_rate_delta: z.number().nullable().optional(),
  total_detail_page_view_rate_percent: z.number().nullable().optional(),

  total_add_to_cart_rate: z.number().nullable(),
  total_add_to_cart_rate_prev: z.number().nullable().optional(),
  total_add_to_cart_rate_delta: z.number().nullable().optional(),
  total_add_to_cart_rate_percent: z.number().nullable().optional(),

  total_new_to_brand_dpv_rate: z.number().nullable(),
  total_new_to_brand_dpv_rate_prev: z.number().nullable().optional(),
  total_new_to_brand_dpv_rate_delta: z.number().nullable().optional(),
  total_new_to_brand_dpv_rate_percent: z.number().nullable().optional(),
};
